import { useConfirmationDialog } from '@justomx/atlas-components'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLoaderData } from 'react-router-dom'

import { Analytics } from 'analytics/analytics.events'
import {
  getTransferDetail,
  updateOrderStatus
} from 'client/stock-transfers/stock-transfers.client'
import { useQueryParam, useRequiredParams } from 'hooks/params.hook'
import {
  OrderDetailSummarySchema,
  type PalletSummaryData
} from 'types/assembly/model'
import { PROGRESS_STATUS, ZOD_ERROR } from 'utils/constants'
import { toastError } from 'utils/toast'
import { useGoToAssembly } from '../assembly.navigator'
import { orderContainersAtom, selectedContainersAtom } from '../Assemby.state'
import { useGoToPreparation } from '../preparation/Preparation.navigator'
import { useGoToProducts } from '../products/Products.navigator'

export const useTransferDetail = () => {
  const { transferId, docNum } = useRequiredParams('transferId', 'docNum')
  const containerPrevScanned = useQueryParam('container')
  const productsPage = useGoToProducts()
  const assemblyPage = useGoToAssembly()
  const preparationPage = useGoToPreparation()
  const [palletsData, setPalletsData] = useState<
    Record<string, PalletSummaryData>
  >({})
  const { open, dialogRef } = useConfirmationDialog()
  const rawPallets = useLoaderData()
  const setContainers = useSetAtom(selectedContainersAtom)
  const setOrderContainers = useSetAtom(orderContainersAtom)
  const loadingRef = useRef(false)
  const [loading, setLoading] = useState(false)

  const hasTriggeredRef = useRef(false)

  const setOrderForPreparation = async () => {
    if (hasTriggeredRef.current) {
      return
    }
    hasTriggeredRef.current = true

    try {
      setLoading(true)
      const result = await updateOrderStatus(transferId, 'readyToWrap')
      Analytics.assembly.automaticUpdateStatus({
        transferId,
        docNum,
        totalPallets: Object.keys(palletsData).length,
        statusFromBackend: result?.orderStatus || 'error'
      })
      preparationPage.go(transferId, docNum)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const formatPalletsData = useCallback(
    (
      pallets: unknown,
      currentState: Record<string, PalletSummaryData> = {}
    ) => {
      const result = OrderDetailSummarySchema.safeParse(pallets)
      if (!result.success) {
        console.info(result.error)
        throw Error(ZOD_ERROR)
      }
      if (
        result.data.summarizedContainers &&
        !result.data.summarizedContainers.length &&
        PROGRESS_STATUS.includes(result.data.status || '') &&
        result.data.pickedFinished
      ) {
        setOrderForPreparation()
        return
      }

      const palletsMap: Record<string, PalletSummaryData> = {}
      result.data.summarizedContainers.forEach((pallet) => {
        const alreadyScanned = currentState[pallet.container]?.scanned
        palletsMap[pallet.container] = {
          ...pallet,
          scanned: pallet.container === containerPrevScanned || alreadyScanned
        }
      })
      setPalletsData(palletsMap)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerPrevScanned]
  )

  useEffect(() => {
    formatPalletsData(rawPallets)
  }, [formatPalletsData, rawPallets])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const updatedRawPallets = await getTransferDetail(transferId)
        formatPalletsData(updatedRawPallets, palletsData)
      } catch (error) {
        console.warn(error)
      } finally {
        setLoading(false)
      }
    }

    const interval = setInterval(fetchData, 30000)
    return () => {
      clearInterval(interval)
    }
  }, [formatPalletsData, palletsData, transferId])

  const result = OrderDetailSummarySchema.safeParse(rawPallets)
  if (!result.success) {
    console.info(result.error)
    throw Error(ZOD_ERROR)
  }

  const atLeastOnePalletScanned = useMemo(() => {
    return Object.values(palletsData).some((pallet) => pallet.scanned)
  }, [palletsData])

  const scannedPallets = useMemo(() => {
    return Object.values(palletsData)
      .filter(({ scanned }) => scanned)
      .map(({ container }) => container)
  }, [palletsData])

  const restartLoading = () => {
    setTimeout(() => {
      console.info('restarting multiple scan validation')
      loadingRef.current = false
    }, 1500)
  }

  const onScanPallet = (scanned: string) => {
    if (loadingRef.current) {
      console.info('skipping scan container due to current operation')
      return
    }
    loadingRef.current = true
    if (!palletsData[scanned]) {
      console.info('pallet not found')
      restartLoading()
      toastError('Tarima no encontrada, intente de nuevo.')
      return
    }

    setPalletsData((prev) => ({
      ...prev,
      [scanned]: { ...prev[scanned], scanned: !prev[scanned].scanned }
    }))

    restartLoading()
    console.info('pallet scanned')
  }

  const onContinue = () => {
    setOrderContainers(Object.keys(palletsData))
    setContainers(scannedPallets)
    productsPage.go(transferId, docNum, scannedPallets)
  }

  const onGoBack = () => {
    assemblyPage.go()
  }

  const openKeyboard = () => {
    open()
  }

  return {
    docNum,
    palletsData,
    atLeastOnePalletScanned,
    dialogRef,
    scannedPallets,
    loading,
    actions: { onScanPallet, onContinue, onGoBack, openKeyboard }
  }
}
